import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQuery } from "@apollo/client";


import {
  LISTAR_ESTADOS,
  LISTAR_CIDADES,
  Estado,
  Cidade,
} from "../../services/localizacaoService";
import LocalizacaoMapaEditor from "../local/LocalizacaoMapaEditor";
import { buscarEnderecoPorCep } from "../../services/cepService";

export interface LocalModalForm {
  nome: string;
  descricao?: string;
  cep: string;
  logradouro: string;
  numero: string;
  bairro: string;
  complemento?: string;
  uf: string;
  cidadeId: string;
  urlLocalizacao?: string | null;
}

export interface LocalModalProps {
  open: boolean;
  initialValues?: Partial<LocalModalForm>;
  saving: boolean;
  onSave: (data: LocalModalForm) => void;
  onClose: () => void;
  localTorneio?: {
    id: string;
    nome: string;
    endereco: {
      logradouro: string;
      numero: string;
      complemento?: string | null;
      bairro: string;
      cidade: {
        nome: string;
        uf: string;
      };
    };
  } | null;
  onUseLocalTorneio?: () => void;
  onUnuseLocalTorneio?: () => void;
  usarLocalTorneio?: boolean;
}

const formatCep = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const LocalModal: React.FC<LocalModalProps> = ({
  open,
  initialValues,
  saving,
  onSave,
  onClose,
  localTorneio,
  onUseLocalTorneio,
  onUnuseLocalTorneio,
  usarLocalTorneio = false,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<LocalModalForm>({
    defaultValues: {
      nome: '',
      descricao: '',
      cep: '',
      logradouro: '',
      numero: '',
      bairro: '',
      complemento: '',
      uf: '',
      cidadeId: '',
      urlLocalizacao: '',
    },
  });

  const [ufs, setUfs] = useState<Estado[]>([]);
  const [cidades, setCidades] = useState<Cidade[]>([]);
  const [filteredCidades, setFilteredCidades] = useState<Cidade[]>([]);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [cepErro, setCepErro] = useState<string | null>(null);
  const [enderecoCepEncontrado, setEnderecoCepEncontrado] = useState<{ localidade: string; uf: string } | null>(null);

  const { data: estadosData } = useQuery<{ estados: Estado[] }>(LISTAR_ESTADOS);
  const { data: cidadesData } = useQuery<{ cidades: Cidade[] }>(LISTAR_CIDADES);

  useEffect(() => {
    if (open) {
      if (estadosData) {
        setUfs(estadosData.estados);
      }
      if (cidadesData) {
        setCidades(cidadesData.cidades);
      }
      if (initialValues) {
        reset({
          nome: initialValues.nome || '',
          descricao: initialValues.descricao || '',
          cep: initialValues.cep ? formatCep(initialValues.cep) : '',
          logradouro: initialValues.logradouro || '',
          numero: initialValues.numero || '',
          bairro: initialValues.bairro || '',
          complemento: initialValues.complemento || '',
          uf: initialValues.uf || '',
          cidadeId: initialValues.cidadeId || '',
          urlLocalizacao: initialValues.urlLocalizacao ?? '',
        });
        if (initialValues.uf && cidadesData) {
          const cidadesFiltradas = cidadesData.cidades.filter(
            (cidade) => cidade.uf === initialValues.uf
          );
          setFilteredCidades(cidadesFiltradas);
        }
      } else {
        reset();
        setFilteredCidades([]);
      }
    }
  }, [open, initialValues, reset]);

  const selectedUf = watch('uf');
  // Sincroniza `filteredCidades` sempre que `selectedUf`/`cidadesData` mudam —
  // cobre tanto a troca de UF pelo usuário quanto o caso em que `cidadesData`
  // (useQuery) resolve DEPOIS do reset() inicial (efeito acima só roda quando
  // `open`/`initialValues`/`reset` mudam, não quando a query resolve). Não
  // limpa `cidadeId` aqui — limpar em reação a QUALQUER mudança destes deps
  // também disparava na hidratação inicial (reset() -> selectedUf muda de ''
  // para o UF de `initialValues` -> quando `cidadesData` resolve em seguida,
  // este efeito rodava de novo e apagava o `cidadeId` que o reset() acabou de
  // preencher). A limpeza de `cidadeId` por troca de UF passa a ser só no
  // `onChange` do próprio select de UF (interação real do usuário).
  useEffect(() => {
    if (selectedUf && cidadesData) {
      const cidadesFiltradas = cidadesData.cidades.filter(
        (cidade) => cidade.uf === selectedUf
      );
      setFilteredCidades(cidadesFiltradas);
    }
  }, [selectedUf, cidadesData]);

  // Sincroniza `ufs` sempre que `estadosData` (useQuery) resolver — mesmo
  // raciocínio do efeito de `filteredCidades`/`cidadesData` acima: o efeito de
  // hidratação inicial só roda quando `open`/`initialValues`/`reset` mudam, e
  // `estadosData` normalmente ainda não chegou nesse instante (é uma query
  // Apollo assíncrona). Sem este efeito, o <select> de UF nunca ganha as
  // <option>s reais e um valor programático (ex.: `buscarCep`, AC-25) não
  // aparece selecionado por faltar a <option> correspondente.
  useEffect(() => {
    if (estadosData) {
      setUfs(estadosData.estados);
    }
  }, [estadosData]);

  // Casa a cidade retornada pelo ViaCEP (AC-25) assim que `cidadesData`
  // estiver disponível — mesmo raciocínio dos dois efeitos acima. `buscarCep`
  // só GRAVA o par localidade/UF encontrado; o casamento em si é reativo,
  // porque `cidadesData` (useQuery) pode resolver DEPOIS de `buscarCep` já ter
  // terminado (a chamada ao ViaCEP, mockada em teste, resolve via microtask;
  // a query Apollo pode levar mais tempo) — sem isto, a cidade nunca seria
  // selecionada quando a query ainda não tivesse chegado no instante do clique.
  useEffect(() => {
    if (enderecoCepEncontrado && cidadesData) {
      const match = cidadesData.cidades.find(
        (c) =>
          c.nome.toLowerCase() === enderecoCepEncontrado.localidade.toLowerCase() &&
          c.uf.toLowerCase() === enderecoCepEncontrado.uf.toLowerCase()
      );
      if (match) {
        setValue('cidadeId', match.id);
      }
    }
  }, [enderecoCepEncontrado, cidadesData, setValue]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && e.ctrlKey) {
        handleSubmit(onSave)();
      }
    };
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = ''; // Restore scrolling
    };
  }, [open, handleSubmit, onSave, onClose]);

  const buscarCep = async () => {
    setCepErro(null);
    const cepValue = watch('cep');
    if (!cepValue) {
      setCepErro('Informe o CEP');
      return;
    }
    setBuscandoCep(true);
    try {
      const endereco = await buscarEnderecoPorCep(cepValue);
      setValue('logradouro', endereco.logradouro || '');
      setValue('bairro', endereco.bairro || '');
      setValue('uf', endereco.uf || '');
      setEnderecoCepEncontrado({ localidade: endereco.localidade || '', uf: endereco.uf || '' });
    } catch (err: any) {
      setCepErro(err.message === 'CEP não encontrado' ? err.message : 'Erro ao buscar CEP.');
    } finally {
      setBuscandoCep(false);
    }
  };

  if (!open) return null;

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div
        className="modal fade show d-block"
        role="dialog"
        tabIndex={-1}
        onMouseDown={handleBackdrop}
      >
        <div className="modal-dialog modal-lg" role="document" style={{ maxHeight: '90vh', margin: '5vh auto' }}>
          <form className="modal-content" onSubmit={handleSubmit(onSave)} style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h5 className="modal-title w-100 text-center">Local do Torneio</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Fechar"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
              {localTorneio && onUseLocalTorneio && (
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="usar-local-torneio"
                      checked={usarLocalTorneio}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onUseLocalTorneio?.();
                        } else {
                          onUnuseLocalTorneio?.();
                        }
                      }}
                      className="form-check-input"
                    />
                    <label htmlFor="usar-local-torneio" className="form-check-label">
                      Usar local do torneio
                    </label>
                  </div>
                  {usarLocalTorneio && (
                    <div className="mt-2 p-2 bg-light rounded">
                      <div className="fw-bold">{localTorneio.nome}</div>
                      <div className="text-muted small">
                        {localTorneio.endereco.logradouro}, {localTorneio.endereco.numero}
                        {localTorneio.endereco.complemento ? ` - ${localTorneio.endereco.complemento}` : ''}, {localTorneio.endereco.bairro}, {localTorneio.endereco.cidade.nome}/{localTorneio.endereco.cidade.uf}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {(!usarLocalTorneio || !localTorneio) && (
                <>
                  <h6 className="mb-3">Local</h6>
              <div className="mb-3">
                <label className="form-label">Nome</label>
                <input
                  className="form-control"
                  {...register('nome', {
                    required: 'Nome é obrigatório',
                    minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                  })}
                />
                {errors.nome && (
                  <span className="text-danger small">{errors.nome.message}</span>
                )}
              </div>
              <div className="mb-3 ">
                <label className="form-label">Descrição</label>
                <textarea className="form-control" rows={3} {...register('descricao')} />
              </div>
              <div className="mb-3">
                <label className="form-label">Localização no mapa</label>
                <Controller
                  name="urlLocalizacao"
                  control={control}
                  render={({ field }) => (
                    <LocalizacaoMapaEditor
                      // `key` força remount quando o valor externo muda (ex.:
                      // `reset()` do useEffect acima, síncrono a `initialValues`,
                      // roda um tick depois do 1º render) — o input de URL do
                      // LocalizacaoMapaEditor é não-controlado (`defaultValue`
                      // só aplicado na montagem); sem o `key`, o React ignora
                      // mudanças de `defaultValue` em updates e o campo ficaria
                      // preso no valor vazio do 1º render mesmo após o reset.
                      key={field.value ?? ''}
                      urlLocalizacao={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
              <h6 className="mb-3 mt-4">Endereço</h6>
              <div className="row">
                <div className="mb-3 col-xl-3">
                  <label className="form-label">CEP</label>
                  <div className="d-flex gap-1">
                    <Controller
                      name="cep"
                      control={control}
                      rules={{
                        required: 'CEP obrigatorio',
                        pattern: {
                          value: /^\d{5}-\d{3}$/,
                          message: 'CEP invalido',
                        },
                      }}
                      render={({ field }) => (
                        <input
                          {...field}
                          className="form-control"
                          onChange={(event) => field.onChange(formatCep(event.target.value))}
                        />
                      )}
                    />
                    <button
                      type="button"
                      className="btn btn-light btn-sm"
                      onClick={buscarCep}
                      disabled={buscandoCep}
                    >
                      {buscandoCep ? 'Buscando...' : 'Buscar'}
                    </button>
                  </div>
                  {errors.cep && (
                    <span className="text-danger small">{errors.cep.message}</span>
                  )}
                  {cepErro && (
                    <span className="text-danger small d-block">{cepErro}</span>
                  )}
                </div>
                <div className="mb-3 col-xl-6">
                  <label className="form-label">Logradouro</label>
                  <input
                    className="form-control"
                    {...register('logradouro', { required: 'Logradouro é obrigatório' })}
                  />
                  {errors.logradouro && (
                    <span className="text-danger small">{errors.logradouro.message}</span>
                  )}
                </div> 
                <div className="mb-3 col-xl-3">
                  <label className="form-label">Número</label>
                  <input
                    className="form-control"
                    {...register('numero', { required: 'Número é obrigatório' })}
                  />
                  {errors.numero && (
                    <span className="text-danger small">{errors.numero.message}</span>
                  )}
                </div>
                <div className="mb-3 col-xl-3">
                  <label className="form-label">Bairro</label>
                  <input
                    className="form-control"
                    {...register('bairro', { required: 'Bairro é obrigatório' })}
                  />
                  {errors.bairro && (
                    <span className="text-danger small">{errors.bairro.message}</span>
                  )}
                </div>
                <div className="mb-3 col-xl-3">
                  <label className="form-label">Complemento</label>
                  <input className="form-control" {...register('complemento')} />
                </div>
                <div className="mb-3 col-xl-3">
                  <label className="form-label">UF</label>
                  <Controller
                    name="uf"
                    control={control}
                    rules={{ required: 'UF é obrigatório' }}
                    render={({ field }) => (
                      // `select` CONTROLADO (via Controller/`value`, não
                      // `register()` + ref não-controlado): React re-sincroniza
                      // a seleção a cada render comparando `value` às <option>
                      // atuais — por isso a seleção aplicada por `reset()`
                      // (initialValues) sobrevive mesmo que `ufs` (useQuery)
                      // ainda não tivesse carregado a UF no momento do reset.
                      // Um <select> não-controlado (ref-based) NÃO tem essa
                      // sincronização: se a <option> correspondente ainda não
                      // existir no instante em que o valor é atribuído, o
                      // navegador descarta a seleção em silêncio e ela não é
                      // reaplicada quando a opção aparece depois.
                      <select
                        className="form-select"
                        name={field.name}
                        ref={field.ref}
                        value={field.value ?? ''}
                        onBlur={field.onBlur}
                        onChange={(event) => {
                          field.onChange(event);
                          // Limpa a cidade selecionada só em resposta a uma
                          // troca de UF real do usuário (este onChange só
                          // dispara em interação de fato) — nunca no efeito
                          // reativo de `selectedUf`/`cidadesData` acima, que
                          // também roda na hidratação inicial vinda de
                          // `initialValues`.
                          setValue('cidadeId', '');
                        }}
                      >
                        <option value="">Selecione</option>
                        {ufs.map((uf) => (
                          <option key={uf.uf} value={uf.uf}>
                            {uf.uf}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.uf && (
                    <span className="text-danger small">{errors.uf.message}</span>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Cidade</label>
                  <Controller
                    name="cidadeId"
                    control={control}
                    rules={{ required: 'Cidade é obrigatória' }}
                    render={({ field }) => (
                      // Mesmo raciocínio de `select` controlado do UF acima.
                      <select
                        className="form-select"
                        name={field.name}
                        ref={field.ref}
                        value={field.value ?? ''}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                      >
                        <option value="">Selecione</option>
                        {filteredCidades.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.nome}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.cidadeId && (
                    <span className="text-danger small">{errors.cidadeId.message}</span>
                  )}
                </div>
              </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-link"
                onClick={onClose}
              >
                {usarLocalTorneio ? 'Fechar' : 'Cancelar'}
              </button>
              {(!usarLocalTorneio || !localTorneio) && (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default LocalModal;

